import type { CSSProperties } from "react";

/** Style slots of the connection form, overridable with `classNames` / `styles`. */
export type FormSlot =
  | "card"
  | "title"
  | "field"
  | "label"
  | "input"
  | "hint"
  | "button"
  | "buttonPrimary"
  | "status"
  | "menu"
  | "option";

export type FormTheme = {
  classNames?: Partial<Record<FormSlot, string>>;
  styles?: Partial<Record<FormSlot, CSSProperties>>;
};

const LINE = "rgba(15,23,42,.15)";

export const FORM_BASE: Record<FormSlot, CSSProperties> = {
  card: {
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 14,
    padding: "18px 20px",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(15,23,42,.04)",
    fontFamily: "inherit",
    color: "inherit",
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    margin: 0,
    paddingBottom: 10,
    borderBottom: "1px solid rgba(15,23,42,.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  field: { display: "block", minWidth: 0 },
  label: { display: "block", fontSize: 15, fontWeight: 600, margin: "16px 0 8px", opacity: 0.85 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    padding: "14px 16px",
    font: "inherit",
    fontSize: 16,
    background: "transparent",
    color: "inherit",
    textAlign: "left",
  },
  hint: { fontSize: 14, opacity: 0.55, lineHeight: 1.45, flex: 1 },
  button: {
    border: "none",
    background: "transparent",
    color: "inherit",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    padding: "8px 10px",
    font: "inherit",
    whiteSpace: "nowrap",
  },
  buttonPrimary: {
    border: "none",
    background: "#0f172a",
    color: "#fff",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    padding: "10px 16px",
    borderRadius: 10,
    font: "inherit",
    whiteSpace: "nowrap",
  },
  status: { fontSize: 12.5, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 7 },
  menu: {
    position: "absolute",
    zIndex: 20,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    margin: 0,
    padding: 4,
    listStyle: "none",
    maxHeight: 240,
    overflowY: "auto",
    background: "#fff",
    color: "#0f172a",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(15,23,42,.12)",
  },
  option: {
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
};

/** What a slot keeps even when the host styles it with a class of its own: its structure,
 *  never its look. */
const STRUCTURE = new Set<keyof CSSProperties>([
  "display",
  "width",
  "boxSizing",
  "textAlign",
  "position",
  "zIndex",
  "top",
  "left",
  "right",
  "listStyle",
  "maxHeight",
  "overflowY",
  "alignItems",
  "justifyContent",
  "gap",
  "flex",
  "minWidth",
  "whiteSpace",
]);

const structural = (style: CSSProperties): CSSProperties =>
  Object.fromEntries(
    Object.entries(style).filter(([key]) => STRUCTURE.has(key as keyof CSSProperties)),
  ) as CSSProperties;

/**
 * `{className, style}` for a slot: base, then local extras, then the host's overrides.
 *
 * **A host class wins over the base look.** Inline styles beat any class, so a base
 * `padding: 14px` and `fontSize: 16` sat on top of a host `.input { height: 38px }` and cut
 * the text of the dropdowns in half. With a class for the slot, the base keeps only its
 * structure (width, display, positioning); padding, font, border and colors are the host's.
 */
export const formSlot = (theme: FormTheme, name: FormSlot, extra?: CSSProperties) => {
  const className = theme.classNames?.[name];
  const base = className ? structural(FORM_BASE[name]) : FORM_BASE[name];
  return { className, style: { ...base, ...extra, ...(theme.styles?.[name] ?? {}) } };
};
